import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, Check, PackageCheck, Tag, Warehouse, ShieldCheck,
  Truck, DollarSign, Boxes, RefreshCw,
} from "lucide-react";
import { COMPANY } from "../data/mock";

const WHAT_WE_BUY = [
  { icon: Tag, title: "Amazon-Branded Merchandise", text: "Overstock of Amazon private-label lines such as Amazon Basics, Solimo, and similar in-house brands." },
  { icon: Boxes, title: "White-Label & Private-Label Goods", text: "Unbranded or private-label products produced for online sellers that need a discreet resale channel." },
  { icon: Warehouse, title: "FBA Overstock & Aged Stock", text: "Units sitting in fulfillment centers that are racking up long-term storage fees and tying up capital." },
  { icon: RefreshCw, title: "Removal-Order & Returned Units", text: "Inventory pulled through removal orders, customer returns, and LPN/returns loads ready to move." },
];

const BENEFITS = [
  { icon: DollarSign, title: "Fast, Full Payment", text: "We buy the whole lot and wire payment on receipt — no drip-feeding units or waiting on payouts." },
  { icon: ShieldCheck, title: "Off-Marketplace & Discreet", text: "We resell through quiet, offline channels so your listings and pricing on the platform stay protected." },
  { icon: Warehouse, title: "Stop the Storage Bleed", text: "Clear aged FBA stock in one move and put an end to mounting long-term storage fees." },
  { icon: Truck, title: "We Handle the Logistics", text: "From removal coordination to freight, our team manages pickup and transport end to end." },
];

export default function AmazonOverstock() {
  return (
    <main>
      {/* HERO */}
      <section className="relative overflow-hidden bg-[var(--brand-hero)]">
        <div className="hero-blob pointer-events-none absolute -right-40 -top-40 h-[500px] w-[500px] rounded-full" />
        <div className="mx-auto grid max-w-7xl items-center gap-10 px-5 py-16 lg:grid-cols-2 lg:py-24 lg:px-8">
          <div className="animate-fade-up">
            <span className="mb-5 inline-flex items-center gap-2 rounded-full bg-white px-4 py-1.5 text-xs font-semibold text-teal-brand shadow-sm">
              <PackageCheck size={14} /> For Amazon & Online Sellers
            </span>
            <h1 className="text-4xl font-extrabold leading-tight text-navy-brand sm:text-5xl">
              We Buy Amazon Overstock
            </h1>
            <p className="mt-6 max-w-xl text-[15px] leading-relaxed text-slate-500">
              Sitting on surplus Amazon-branded or white-label merchandise? We purchase overstock,
              aged FBA inventory, removal-order units, and private-label goods outright — clearing your
              fulfillment centers and turning idle stock back into cash, quickly and quietly.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <Link
                to="/submit-inventory"
                className="group flex items-center gap-2 rounded-full bg-navy-brand px-7 py-3.5 text-sm font-semibold text-white transition-all duration-300 hover:bg-teal-brand hover:shadow-xl"
              >
                Submit Your Overstock
                <ArrowRight size={17} className="transition-transform group-hover:translate-x-1" />
              </Link>
              <a href={COMPANY.phoneHref} className="text-sm font-semibold text-navy-brand hover:text-teal-brand">
                or call {COMPANY.phone}
              </a>
            </div>
          </div>
          <div className="relative animate-floaty">
            <img
              src="https://images.unsplash.com/photo-1624137527136-66e631bdaa0e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwxfHxsaXF1aWRhdGlvbiUyMGJveGVzfGVufDB8fHx8MTc4NzA5MTQ0NXww&ixlib=rb-4.1.0&q=85"
              alt="Overstock cartons ready to ship"
              className="w-full rounded-3xl object-cover shadow-2xl"
              style={{ maxHeight: 460 }}
            />
            <div className="absolute -bottom-6 -left-6 hidden rounded-2xl bg-white p-5 shadow-xl sm:block">
              <div className="flex items-center gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-teal-brand text-white">
                  <Warehouse size={22} />
                </div>
                <div>
                  <p className="text-lg font-extrabold text-navy-brand">Clear Your FBA</p>
                  <p className="text-xs text-slate-500">Stop long-term storage fees</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WHAT WE BUY */}
      <section className="mx-auto max-w-7xl px-5 py-20 lg:px-8">
        <div className="mx-auto max-w-2xl text-center">
          <span className="text-sm font-bold uppercase tracking-wider text-teal-brand">What We Purchase</span>
          <h2 className="mt-3 text-3xl font-extrabold text-navy-brand sm:text-4xl">
            Amazon-branded &amp; white-label merchandise
          </h2>
          <p className="mt-4 text-[15px] leading-relaxed text-slate-500">
            Whether it's in-house Amazon brands or unbranded private-label product, if it's finished
            consumer goods, we're likely a buyer. Here's what we take on most often.
          </p>
        </div>
        <div className="mt-12 grid gap-8 sm:grid-cols-2">
          {WHAT_WE_BUY.map((x) => {
            const Icon = x.icon;
            return (
              <div key={x.title} className="group flex gap-5 rounded-2xl border border-slate-100 bg-white p-7 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl">
                <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-teal-brand/10 text-teal-brand transition-colors group-hover:bg-teal-brand group-hover:text-white">
                  <Icon size={26} />
                </div>
                <div>
                  <h3 className="mb-2 text-[17px] font-bold text-navy-brand">{x.title}</h3>
                  <p className="text-sm leading-relaxed text-slate-500">{x.text}</p>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* BENEFITS */}
      <section className="bg-slate-50">
        <div className="mx-auto max-w-7xl px-5 py-20 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <span className="text-sm font-bold uppercase tracking-wider text-teal-brand">Why Sell to Us</span>
            <h2 className="mt-3 text-3xl font-extrabold text-navy-brand sm:text-4xl">
              The simplest way to clear Amazon overstock
            </h2>
          </div>
          <div className="mt-12 grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
            {BENEFITS.map((b) => {
              const Icon = b.icon;
              return (
                <div key={b.title} className="rounded-2xl bg-white p-7 shadow-sm">
                  <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-teal-brand/10 text-teal-brand">
                    <Icon size={26} />
                  </div>
                  <h3 className="mb-2 text-[16px] font-bold text-navy-brand">{b.title}</h3>
                  <p className="text-sm leading-relaxed text-slate-500">{b.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-7xl px-5 py-16 lg:px-8">
        <div className="relative overflow-hidden rounded-3xl bg-navy-brand px-6 py-12 text-center text-white lg:px-12">
          <div className="hero-blob pointer-events-none absolute -right-24 -top-24 h-80 w-80 rounded-full" />
          <h2 className="relative text-2xl font-extrabold sm:text-3xl">Ready to move your Amazon overstock?</h2>
          <p className="relative mx-auto mt-3 max-w-xl text-sm text-slate-300">
            Send over your inventory sheet and our buyers will review it and follow up with an offer —
            fast, discreet, and completely off-marketplace.
          </p>
          <div className="relative mt-7 flex flex-wrap justify-center gap-4">
            <Link
              to="/submit-inventory"
              className="rounded-full bg-teal-brand px-7 py-3.5 text-sm font-semibold text-white transition-transform hover:scale-105"
            >
              Submit Your Inventory
            </Link>
            <a
              href={`mailto:${COMPANY.email}`}
              className="rounded-full border border-white/25 px-7 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-white/10"
            >
              Email {COMPANY.email}
            </a>
          </div>
        </div>
      </section>
    </main>
  );
}
